import { FootprintPolygonsType } from 'data/types';
import { useMemo } from 'react';
import { footprintPolygonsToPixelPath } from 'utils/footprint';
import { placeFootprintAreaLabels } from 'utils/footprint-area-label-placement';
import { isValidFootprintShape } from 'utils/footprint-union';
import FootprintAreaLabel from './footprint-area-label';

type FootprintOverlayProps = Readonly<{
  polygons: FootprintPolygonsType;
  showArea?: boolean;
  faint?: boolean;
  pathId?: string;
}>;

export default function FootprintOverlay({
  polygons,
  showArea = false,
  faint = false,
  pathId
}: FootprintOverlayProps) {
  const validPolygons = useMemo(
    () => polygons.filter(isValidFootprintShape),
    [polygons]
  );

  const pathString = useMemo(
    () => footprintPolygonsToPixelPath(validPolygons),
    [validPolygons]
  );

  const areaLabels = useMemo(
    () => (showArea ? placeFootprintAreaLabels(validPolygons) : []),
    [validPolygons, showArea]
  );

  if (validPolygons.length === 0 || !pathString) {
    return null;
  }

  return (
    <svg
      className='pointer-events-none absolute inset-0 z-[5] h-full w-full overflow-visible'
      style={{ overflow: 'visible' }}
    >
      <path
        id={pathId}
        d={pathString}
        fill='none'
        stroke='#000000'
        strokeWidth={faint ? 2 : 4}
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeDasharray={faint ? '8 6' : undefined}
        opacity={1}
      />
      {areaLabels.map(
        (placement, index) =>
          placement && (
            <FootprintAreaLabel
              // eslint-disable-next-line react/no-array-index-key
              key={`footprint-area-${index}`}
              anchor={placement.anchor}
              areaLabel={placement.areaLabel}
              dimensionsLabel={placement.dimensionsLabel}
            />
          )
      )}
    </svg>
  );
}
